import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { loginAPI, adminLoginAPI, getCurrentUserAPI, LoginDTO, LoginResponse, ApiResponse } from "./loginAPI";
import { DEFAULT_STORE_TIMEZONE, normalizeCurrencySymbol } from '../../types/store';

interface AuthState {
  user: LoginResponse["user"] | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  isInitialized: boolean; // Track if auth state has been initialized
  activeStoreId: string | null; // Currently selected store
  activeStoreCurrency: string; // Active store's currency code
  activeStoreCurrencySymbol: string; // Active store's currency symbol
  activeStoreTimezone: string; // IANA timezone of the active store
}

// Load initial state from localStorage synchronously
const loadInitialState = (): AuthState => {
  try {
    const token = localStorage.getItem("token");
    const userStr = localStorage.getItem("user");
    const activeStoreId = localStorage.getItem("active-store-id");
    const activeStoreCurrency = localStorage.getItem("store-currency") || 'MYR';
    const activeStoreCurrencySymbol = normalizeCurrencySymbol(
      activeStoreCurrency,
      localStorage.getItem("store-currency-symbol") || 'RM',
    );
    const activeStoreTimezone = localStorage.getItem("store-timezone") || DEFAULT_STORE_TIMEZONE;
    
    if (token && userStr) {
      const user = JSON.parse(userStr);
      return {
        user,
        token,
        loading: false,
        error: null,
        isInitialized: true,
        activeStoreId: activeStoreId || user.storeIds?.[0] || null,
        activeStoreCurrency,
        activeStoreCurrencySymbol,
        activeStoreTimezone,
      };
    }
  } catch (error) {
    console.error("Error loading auth state from localStorage:", error);
    // Clear corrupted data
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }
  
  return {
    user: null,
    token: null,
    loading: false,
    error: null,
    isInitialized: true,
    activeStoreId: null,
    activeStoreCurrency: 'MYR',
    activeStoreCurrencySymbol: 'RM',
    activeStoreTimezone: DEFAULT_STORE_TIMEZONE,
  };
};

const initialState: AuthState = loadInitialState();

// Async thunk for login
export const loginUser = createAsyncThunk<
  LoginResponse, // return type when resolved
  LoginDTO,      // argument type
  { rejectValue: string }
>("/login", async (dto, { rejectWithValue }) => {
  try {
    const res: ApiResponse<LoginResponse> = await loginAPI(dto);

    if (res.status && res.data) {
      return res.data;
    } else {
      return rejectWithValue(res.message || "Login failed");
    }
  } catch {
    return rejectWithValue("Server error. Please try again later.");
  }
});

// Async thunk for admin login
export const adminLoginUser = createAsyncThunk<
  LoginResponse, // return type when resolved
  LoginDTO,      // argument type
  { rejectValue: string }
>("/admin/login", async (dto, { rejectWithValue }) => {
  try {
    const res: ApiResponse<LoginResponse> = await adminLoginAPI(dto);

    if (res.status && res.data) {
      return res.data;
    } else {
      return rejectWithValue(res.message || "Admin login failed");
    }
  } catch {
    return rejectWithValue("Server error. Please try again later.");
  }
});

// Async thunk to refresh current user (fetch updated permissions)
export const refreshCurrentUser = createAsyncThunk<
  { user: LoginResponse['user'] },
  void,
  { rejectValue: string }
>("auth/refreshUser", async (_, { rejectWithValue }) => {
  try {
    const res = await getCurrentUserAPI();

    if (res.status && res.data) {
      return res.data;
    } else {
      return rejectWithValue(res.message || "Failed to fetch user");
    }
  } catch {
    return rejectWithValue("Server error. Please try again later.");
  }
});

const loginSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      // Reset all state to initial values
      state.user = null;
      state.token = null;
      state.error = null;
      state.loading = false;
      state.isInitialized = true;
      state.activeStoreId = null;
      state.activeStoreCurrency = 'MYR';
      state.activeStoreCurrencySymbol = 'RM';
      state.activeStoreTimezone = DEFAULT_STORE_TIMEZONE;
      
      // Clear ALL localStorage items related to user session
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("active-store-id");
      localStorage.removeItem("store-currency");
      localStorage.removeItem("store-currency-symbol");
      localStorage.removeItem("store-timezone");
      
      // Clear sessionStorage as well
      sessionStorage.clear();
      
      // Clear any cached API data
      try {
        // Clear IndexedDB if used
        if (window.indexedDB) {
          window.indexedDB.deleteDatabase('pos-cache');
        }
      } catch (e) {
        console.warn('Could not clear IndexedDB:', e);
      }
    },
    // Action to clear state before new login (called on login page mount)
    clearBeforeLogin(state) {
      state.user = null;
      state.token = null;
      state.error = null;
      state.loading = false;
      state.activeStoreId = null;
      state.activeStoreCurrency = 'MYR';
      state.activeStoreCurrencySymbol = 'RM';
      state.activeStoreTimezone = DEFAULT_STORE_TIMEZONE;
      // Also clear localStorage to ensure fresh start
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("active-store-id");
      localStorage.removeItem("store-currency");
      localStorage.removeItem("store-currency-symbol");
      localStorage.removeItem("store-timezone");
    },
    loadFromStorage(state) {
      // This is now optional since we load synchronously in initialState
      // But keeping for backward compatibility
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");
      if (token && user) {
        state.token = token;
        state.user = JSON.parse(user);
      }
      state.isInitialized = true;
    },
    setActiveStore(state, action: { payload: { storeId: string; currency: string; currencySymbol: string; timezone?: string } }) {
      const normalizedSymbol = normalizeCurrencySymbol(action.payload.currency, action.payload.currencySymbol);
      state.activeStoreId = action.payload.storeId;
      state.activeStoreCurrency = action.payload.currency;
      state.activeStoreCurrencySymbol = normalizedSymbol;
      state.activeStoreTimezone = action.payload.timezone || DEFAULT_STORE_TIMEZONE;
      localStorage.setItem("active-store-id", action.payload.storeId);
      localStorage.setItem("store-currency", action.payload.currency);
      localStorage.setItem("store-currency-symbol", normalizedSymbol);
      localStorage.setItem("store-timezone", state.activeStoreTimezone);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        state.error = null;

        // persist to localStorage
        localStorage.setItem("token", action.payload.accessToken);
        localStorage.setItem("user", JSON.stringify(action.payload.user));
        
        // Set active store to first store by default
        const firstStoreId = action.payload.user.storeIds?.[0] || null;
        state.activeStoreId = firstStoreId;
        if (firstStoreId) {
          localStorage.setItem("active-store-id", firstStoreId);
        }
        
        // Save currency settings from user's store
        const currency = action.payload.user.storeCurrency || 'MYR';
        const currencySymbol = normalizeCurrencySymbol(currency, action.payload.user.storeCurrencySymbol || 'RM');
        state.activeStoreCurrency = currency;
        state.activeStoreCurrencySymbol = currencySymbol;
        state.activeStoreTimezone = action.payload.user.stores?.find((store) => store.store_id === firstStoreId)?.timezone || DEFAULT_STORE_TIMEZONE;
        localStorage.setItem("store-currency", currency);
        localStorage.setItem("store-currency-symbol", currencySymbol);
        localStorage.setItem("store-timezone", state.activeStoreTimezone);
        
        // Clear notification sound flag for fresh login
        sessionStorage.removeItem("notification_initial_sound_played");
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
      })
      // Admin login handlers
      .addCase(adminLoginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminLoginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.accessToken;
        state.error = null;

        // persist to localStorage
        localStorage.setItem("token", action.payload.accessToken);
        localStorage.setItem("user", JSON.stringify(action.payload.user));
        
        // Set active store to first store by default (admin now gets all client stores)
        const firstStoreId = action.payload.user.storeIds?.[0] || null;
        state.activeStoreId = firstStoreId;
        if (firstStoreId) {
          localStorage.setItem("active-store-id", firstStoreId);
        }
        
        // Save currency settings from user's store
        const currency = action.payload.user.storeCurrency || 'MYR';
        const currencySymbol = normalizeCurrencySymbol(currency, action.payload.user.storeCurrencySymbol || 'RM');
        state.activeStoreCurrency = currency;
        state.activeStoreCurrencySymbol = currencySymbol;
        state.activeStoreTimezone = action.payload.user.stores?.find((store) => store.store_id === firstStoreId)?.timezone || DEFAULT_STORE_TIMEZONE;
        localStorage.setItem("store-currency", currency);
        localStorage.setItem("store-currency-symbol", currencySymbol);
        localStorage.setItem("store-timezone", state.activeStoreTimezone);
        
        // Clear notification sound flag for fresh login
        sessionStorage.removeItem("notification_initial_sound_played");
      })
      .addCase(adminLoginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Admin login failed";
      })
      // Refresh current user handlers
      .addCase(refreshCurrentUser.pending, (state) => {
        // Don't show loading for background refresh
      })
      .addCase(refreshCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        // Update localStorage with fresh user data
        localStorage.setItem("user", JSON.stringify(action.payload.user));
      })
      .addCase(refreshCurrentUser.rejected, (state, action) => {
        // If refresh fails (e.g., token expired), logout user
        console.error("Failed to refresh user:", action.payload);
        // Optionally logout if token is invalid
        // state.user = null;
        // state.token = null;
        // localStorage.removeItem("token");
        // localStorage.removeItem("user");
      });
  },
});

export const { logout, loadFromStorage, setActiveStore, clearBeforeLogin } = loginSlice.actions;
export default loginSlice.reducer;
